import { useMemo, useState } from "react";

import { Fight, MetricBlock } from "../api";
import PlayerCell from "../components/PlayerCell";
import ProgressBar from "../components/ProgressBar";
import { useReport } from "../context/ReportContext";
import { classFor } from "../data/classes";

interface Row {
  name?: string;
  type?: string;
  icon?: string;
  total: number;
  perSec?: number;
}

const fmt = (n: number) => n.toLocaleString("en-US");

/**
 * Per-combat tabbed view of damage done. The "Total" tab shows the
 * aggregate across the whole report (the original `block` data); each
 * remaining tab corresponds to one fight from `report.fights` and
 * pulls its rows from `report.metricsByFight[fightId].total_damage_done`.
 *
 * Boss fights keep their canonical name; trash pulls (encounterID 0)
 * are numbered "Combat N" in chronological order. Both kinds carry
 * their relative-to-report-start timestamp range so a raid leader can
 * tell at a glance which 30-second window they're inspecting.
 */
export default function TotalDamageDone({ block }: { block: MetricBlock }) {
  const { report } = useReport();
  const [selected, setSelected] = useState<"total" | number>("total");

  // Build the tab list once per report. WCL collapses adjacent
  // combats with no out-of-combat gap into a single "fight" segment,
  // so a non-boss segment can contain several actual pulls — calling
  // them "Combat N" would be misleading. We label them "Section N"
  // instead. Numbering happens *before* the noise filter so trimmed
  // sections just leave gaps in the sequence (e.g. "Section 4 →
  // Section 6"), preserving each tab's mapping to the WCL log even
  // when survivors aren't contiguous.
  //
  // Timestamps are anchored to the first fight in the current scope
  // — `f.startTime` from WCL is report-start-relative, which on a
  // multi-raid log where Karazhan starts 4 hours in would show as
  // `276:28` instead of `0:00`.
  const tabs = useMemo<TabSpec[]>(() => {
    const fights = (report?.fights ?? [])
      .slice()
      .sort((a, b) => (a.startTime ?? 0) - (b.startTime ?? 0));
    const anchor = fights[0]?.startTime ?? 0;
    let sectionN = 0;
    const all = fights.map<TabSpec>((f) => {
      const isBoss = (f.encounterID ?? 0) !== 0;
      let label: string;
      if (isBoss) {
        label = f.name ?? `Boss ${f.id}`;
      } else {
        sectionN += 1;
        label = `Section ${sectionN}`;
      }
      return {
        fightId: f.id,
        label,
        kill: !!f.kill,
        isBoss,
        timeRange: formatRange(
          (f.startTime ?? 0) - anchor,
          (f.endTime ?? 0) - anchor,
        ),
        durationMs: f.durationMs ?? 0,
        fight: f,
      };
    });
    // Drop noise sections: <3 players engaged, or <2000 total damage.
    // Boss fights are always kept regardless — even a 12-second wipe
    // can be useful to inspect.
    return all.filter((t) => {
      if (t.isBoss) return true;
      const fightBlock = report?.metricsByFight?.[String(t.fightId)]?.[
        "total_damage_done"
      ];
      const rows = (fightBlock?.rows ?? []) as { total?: number }[];
      const playerCount = rows.length;
      const totalDamage =
        (fightBlock?.totals?.raidTotal as number | undefined) ??
        rows.reduce((a, r) => a + (r.total ?? 0), 0);
      return playerCount > 2 && totalDamage >= 2000;
    });
  }, [report]);

  // Resolve the currently-selected tab's rows. "total" uses the block
  // we were rendered with (the all-fights aggregate). Anything else
  // pulls per-fight data out of metricsByFight; trash fights now have
  // entries here too thanks to the backend change.
  const { rows, raidTotal, subtitle, badge, title } = useMemo(() => {
    if (selected === "total") {
      const r = (block.rows as unknown as Row[]) ?? [];
      const tot =
        (block.totals?.raidTotal as number | undefined) ??
        r.reduce((a, x) => a + x.total, 0);
      return {
        rows: r,
        raidTotal: tot,
        subtitle: `Across ${tabs.length} fight${tabs.length === 1 ? "" : "s"}`,
        badge: null as React.ReactNode,
        title: "Total Damage Done",
      };
    }
    const tab = tabs.find((t) => t.fightId === selected);
    const fightBlock = report?.metricsByFight?.[String(selected)]?.[
      "total_damage_done"
    ];
    const r = (fightBlock?.rows as unknown as Row[]) ?? [];
    const tot =
      (fightBlock?.totals?.raidTotal as number | undefined) ??
      r.reduce((a, x) => a + x.total, 0);
    return {
      rows: r,
      raidTotal: tot,
      subtitle: tab
        ? `${tab.timeRange} · ${formatDuration(tab.durationMs)}`
        : "",
      badge:
        tab && tab.isBoss ? (
          <KillBadge kill={tab.kill} />
        ) : tab && !tab.isBoss ? (
          <span className="rounded bg-gray-500/15 text-gray-400 text-[10px] px-1.5 py-0.5">
            TRASH
          </span>
        ) : null,
      title: tab?.label ?? `Fight ${selected}`,
    };
  }, [selected, block, tabs, report]);

  if (!tabs.length && (block.rows ?? []).length === 0) {
    return <p className="text-sm text-gray-500">No damage data.</p>;
  }

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => b.total - a.total),
    [rows],
  );

  return (
    <div className="space-y-3">
      <CombatTabs tabs={tabs} selected={selected} onSelect={setSelected} />

      <DamageTable
        title={title}
        subtitle={subtitle}
        badge={badge}
        rows={sortedRows}
        raidTotal={raidTotal}
      />
    </div>
  );
}

interface TabSpec {
  fightId: number;
  label: string;
  kill: boolean;
  isBoss: boolean;
  timeRange: string;
  durationMs: number;
  fight: Fight;
}

/* ----------------------------- Tabs ----------------------------- */

function CombatTabs({
  tabs,
  selected,
  onSelect,
}: {
  tabs: TabSpec[];
  selected: "total" | number;
  onSelect: (s: "total" | number) => void;
}) {
  // The noise filter keeps the tab count modest (typically 10 bosses +
  // a handful of sections), so wrap-on-overflow gives nicer ergonomics
  // than a horizontal scroll strip — same pattern the Boss Damage view
  // uses.
  return (
    <div className="flex flex-wrap gap-1.5">
      <Tab
        active={selected === "total"}
        onClick={() => onSelect("total")}
        label="Total"
      />
      {tabs.map((t) => (
        <Tab
          key={t.fightId}
          active={selected === t.fightId}
          onClick={() => onSelect(t.fightId)}
          label={t.label}
          dot={t.isBoss ? (t.kill ? "kill" : "wipe") : "trash"}
          aside={t.timeRange}
        />
      ))}
    </div>
  );
}

function Tab({
  active,
  onClick,
  label,
  dot,
  aside,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  dot?: "kill" | "wipe" | "trash";
  aside?: string;
}) {
  return (
    <button
      onClick={onClick}
      title={aside ? `${label} · ${aside}` : label}
      className={[
        "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium",
        "transition-colors max-w-[14rem]",
        active
          ? "border-accent/40 bg-accent/15 text-accent"
          : "border-border/60 bg-bg/40 text-gray-300 hover:bg-white/5 hover:text-gray-100",
      ].join(" ")}
    >
      {dot && (
        <span
          className={[
            "inline-block w-1.5 h-1.5 rounded-full flex-shrink-0",
            dot === "kill" && "bg-emerald-400",
            dot === "wipe" && "bg-red-400",
            dot === "trash" && "bg-gray-500",
          ]
            .filter(Boolean)
            .join(" ")}
        />
      )}
      <span className="truncate">{label}</span>
      {aside && (
        <span className="text-[10px] tabular-nums text-gray-500 flex-shrink-0">
          {aside}
        </span>
      )}
    </button>
  );
}

/* ----------------------------- Table ----------------------------- */

function DamageTable({
  title,
  subtitle,
  badge,
  rows,
  raidTotal,
}: {
  title: string;
  subtitle?: string;
  badge?: React.ReactNode;
  rows: Row[];
  raidTotal: number;
}) {
  const top = rows[0]?.total ?? 0;
  const rankPad = String(rows.length).length;

  return (
    <section className="rounded border border-border/60 bg-bg/40">
      <header className="flex items-baseline justify-between gap-2 px-4 pt-3 pb-2">
        <div className="flex items-baseline gap-2">
          <h3 className="text-sm font-semibold">{title}</h3>
          {badge}
        </div>
        {subtitle && <span className="text-xs text-gray-500">{subtitle}</span>}
      </header>
      {!rows.length ? (
        <p className="px-4 py-3 text-xs text-gray-500">No damage recorded.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500">
                <th className="pb-2 pl-4 pr-3 font-medium">#</th>
                <th className="pb-2 pr-3 font-medium">Player</th>
                <th className="pb-2 pr-3 font-medium text-right">Damage</th>
                <th className="pb-2 pr-3 font-medium text-right">DPS</th>
                <th className="pb-2 pr-4 font-medium w-1/3">% of best</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const pct = top ? (r.total / top) * 100 : 0;
                const cls = classFor(r.type);
                const padded = String(i + 1).padStart(rankPad, "0");
                return (
                  <tr
                    key={`${r.name}-${i}`}
                    className="border-t border-border/50 hover:bg-white/5 transition-colors"
                  >
                    <td className="py-1.5 pl-4 pr-3 text-gray-500 tabular-nums">
                      {padded}
                    </td>
                    <td className="py-1.5 pr-3 max-w-[16rem]">
                      <PlayerCell name={r.name} type={r.type} icon={r.icon} />
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums font-medium">
                      {fmt(r.total)}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-gray-400">
                      {r.perSec ? fmt(Math.round(r.perSec)) : "—"}
                    </td>
                    <td className="py-1.5 pr-4">
                      <ProgressBar pct={pct} color={cls.color} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-border">
                <td
                  colSpan={2}
                  className="py-2 pl-4 pr-3 text-[10px] uppercase tracking-wide text-gray-500"
                >
                  Raid total
                </td>
                <td className="py-2 pr-3 text-right tabular-nums font-semibold">
                  {fmt(raidTotal)}
                </td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}

function KillBadge({ kill }: { kill: boolean }) {
  return kill ? (
    <span className="rounded bg-emerald-500/15 text-emerald-300 text-[10px] px-1.5 py-0.5">
      KILL
    </span>
  ) : (
    <span className="rounded bg-red-500/15 text-red-300 text-[10px] px-1.5 py-0.5">
      WIPE
    </span>
  );
}

function formatDuration(ms: number | undefined): string {
  const total = Math.floor((ms ?? 0) / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}

/** Format a fight's [start, end] window as `m:ss–m:ss` relative to the
 *  report start. WCL ships these as ms-from-report-start already. */
function formatRange(
  startMs: number | undefined,
  endMs: number | undefined,
): string {
  return `${stamp(startMs)}–${stamp(endMs)}`;
}

function stamp(ms: number | undefined): string {
  const total = Math.floor((ms ?? 0) / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
